"use client";

import { useState } from "react";
import { ImageBox } from "./ImageBox";
import type { CarouselImage } from "@/src/lib/content";

/**
 * 首頁輪播：對照舊站 EditorjsParser::parseCarousel()（custom-carousel 區塊）
 * 讀 Carousel::loadImagesData() 交給 yii\bootstrap5\Carousel widget 渲染。
 * 這裡用最小可用的左右切換 + 圓點取代 Bootstrap 5 carousel JS 元件。
 */
export function Carousel({ images }: { images: CarouselImage[] }) {
  const [index, setIndex] = useState(0);

  /**
   * ⚠️⚠️ **沒有有效圖 ⇒ 什麼都不輸出**（⛔ 不畫佔位區）。
   *
   * 舊站逐字（CC-Workflow 006-072 R3）：
   * - `legacy/Ccheng_web/common/components/EditorjsParser.php:645-652`
   *   ```php
   *   private function parseCarousel($block, $columnDiv)
   *   {
   *       $wrapper = $this->dom->createElement('div');              // :647
   *       $wrapper->setAttribute('class', "{$this->prefix}-carousel"); // :649
   *       $images = Carousel::loadImagesData($block->data->id);      // :651
   *       if (count($images) <= 0) return false;                     // :652
   *   ```
   *   ⚠️ `$wrapper` 雖然先建好了，但把它接進 DOM 的 `appendChild`
   *   在 :668-672（`return` **之後**）⇒ 空的時候連外框 div 都不會出現。
   * - `legacy/Ccheng_web/common/models/Carousel.php:125-147` `loadHTML()`：
   *   `if (!empty($items)) { … return $carousels; } return null;` ⇒ 空 ⇒ `null`。
   *
   * ⇒ 這裡回 `null`。唯一的產品 consumer 是 `src/lib/editorjs.tsx:115-116`
   *   （`return <Carousel images={images} />;`），React 對 `null` 本來就不渲染
   *   ⇒ ⛔ 不需要額外的版面裁決，也⛔ 不是改全站設計，是**搬回舊行為**。
   *
   * ⚠️ 原本這裡畫的是 220/320px 的灰底提示區「（目前沒有上架中的輪播圖片）」，
   *   ⇒ 舊站前台**從來不會**出現那塊，且會在版面上佔掉一整條高度。
   */
  if (images.length === 0) return null;

  const current = images[index];

  return (
    <section aria-label="首頁輪播" className="relative overflow-hidden bg-stone-100">
      <div className="relative h-[220px] sm:h-[380px]">
        <ImageBox
          src={current.url.startsWith("/") ? current.url : `/${current.url}`}
          alt={current.title ?? ""}
          className="h-full w-full object-cover"
        />
        {current.title ? (
          <p className="absolute bottom-0 left-0 right-0 bg-black/40 px-4 py-2 text-sm text-white">
            {current.title}
          </p>
        ) : null}
      </div>

      {images.length > 1 ? (
        <>
          <button
            type="button"
            aria-label="上一張"
            onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 px-3 py-2 text-white hover:bg-black/50"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="下一張"
            onClick={() => setIndex((i) => (i + 1) % images.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 px-3 py-2 text-white hover:bg-black/50"
          >
            ›
          </button>
          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-2">
            {images.map((img, i) => (
              <button
                key={img.id || i}
                type="button"
                aria-label={`第 ${i + 1} 張`}
                onClick={() => setIndex(i)}
                className={`h-1.5 w-6 rounded-full ${
                  i === index ? "bg-amber-500" : "bg-white/60"
                }`}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
